/**
 * Targeted OHLC loads — symbol-scoped via worker, optional day range (not full DB scan).
 */

import type { OHLCData } from "@/vybe-trader-client/chart/types";

function mergeDedupeSorted(a: OHLCData[], b: OHLCData[]): OHLCData[] {
  if (b.length === 0) return a;
  if (a.length === 0) return [...b].sort((x, y) => x.timestamp - y.timestamp);
  const map = new Map<number, OHLCData>();
  for (const bar of a) map.set(bar.timestamp, bar);
  for (const bar of b) map.set(bar.timestamp, bar);
  return Array.from(map.values()).sort((x, y) => x.timestamp - y.timestamp);
}

/** In-memory chart cache (same source the chart UI uses). */
export async function loadBarsFromChartStore(
  symbol: string,
): Promise<OHLCData[] | null> {
  const { useChartStore3 } = await import(
    "@/vybe-trader-client/chart/stores/useChartStore3"
  );
  const { CHART_PANEL_IDS } = await import(
    "@/vybe-trader-client/chart/stores/chartPanelTypes"
  );

  let merged: OHLCData[] = [];
  for (const panelId of CHART_PANEL_IDS) {
    const slot = useChartStore3.getState().slots[panelId];
    if (slot.selectedSymbol?.currency !== symbol) continue;
    if (slot.ohlcData.length === 0) continue;
    merged = mergeDedupeSorted(merged, slot.ohlcData);
  }
  return merged.length > 0 ? merged : null;
}

function filterBarsByWindow(
  bars: OHLCData[],
  fromTs: number,
  toTs: number,
): OHLCData[] {
  return bars.filter((b) => b.timestamp >= fromTs && b.timestamp <= toTs);
}

/**
 * Load 1m bars for `company_server_SYMBOL` from IndexedDB (worker, key-range only).
 */
export async function loadIdbBarsForSymbol(
  accountKey: string,
  symbol: string,
  window?: { fromTs: number; toTs: number },
): Promise<OHLCData[]> {
  const fullKey = `${accountKey}_${symbol}`;
  const { ohlcIdbDayCache } = await import(
    "@/vybe-trader-client/chart/workers/OhlcIdbCacheManager"
  );

  if (!window) {
    return ohlcIdbDayCache.loadSymbolBars(fullKey);
  }

  return ohlcIdbDayCache.loadSymbolBars(fullKey, {
    fromTs: window.fromTs,
    toTs: window.toTs,
  });
}

export async function loadBarsForSymbol(
  accountKey: string,
  symbol: string,
  window: { fromTs: number; toTs: number },
): Promise<OHLCData[]> {
  const fromChart = await loadBarsFromChartStore(symbol);
  if (fromChart) {
    const sliced = filterBarsByWindow(fromChart, window.fromTs, window.toTs);
    if (sliced.length > 0) {
      return sliced;
    }
  }

  return loadIdbBarsForSymbol(accountKey, symbol, window);
}

export function barsRangeFromSeries(bars: OHLCData[]): {
  minTs: number;
  maxTs: number;
  count: number;
} | null {
  if (bars.length === 0) return null;
  return {
    minTs: bars[0].timestamp,
    maxTs: bars[bars.length - 1].timestamp,
    count: bars.length,
  };
}
